import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import { Fade, InputAdornment, styled, TextField, Tooltip, tooltipClasses } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import ConfirmationModal from 'components/common/ConfirmationModal';
import Footer from 'components/common/Footer/Footer';
import HeaderLogo from 'components/common/HeaderLogo/HeaderLogo';
import ExternalClientAccessInfo from 'components/common/ExternalClientAccessInfo/ExternalClientAccessInfo';
import NumberFormatCustom from 'components/common/NumberFormatCustom';
import Summary from 'components/common/Summary/Summary';
import information from 'img/information.svg';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getNewCustomSpace } from 'services/inSite/inSite';
import actions from 'store/actions';
import { updateNA, updateExternalAccessNA } from 'store/na/thunk';
import { getQuantity, getTotalSF } from 'utils/categories';
import { addThousandSeparator } from 'utils/numbers';
import CustomSpaceDrawer from './CustomSpacedrawer';
import SpaceDrawer from './Spacedrawer';
import styles from './step5.module.scss';
import { ReactComponent as DeleteIcon } from 'icons/delete.svg';
import { ReactComponent as EditIcon } from 'icons/edit.svg';
import { ReactComponent as SearchIcon } from 'icons/search.svg';
import { useTranslation } from 'react-i18next';
import { getUnits } from 'utils/units';
import { getFormatSeparatorsOptions } from 'config/marketConfig';
import { weakKey } from 'utils/objects';
import searchParams from 'constants/searchParams';
import paths from 'constants/paths';
import { useStepsWizard } from 'hooks/useStepsWizard';

const defaultSpaceType = {
  id: 0,
  name: '',
  displayOrder: 0,
  customSpace: false,
  spaces: [],
};
const defaultCustomSpace = { name: '', quantity: 0, width: 0, height: 0 };

export default function Step5() {
  const quantityBounds = {
    quantityMinimum: 0,
    quantityMaximum: 10000,
  };

  const dispatch = useDispatch();
  const params = new URLSearchParams(window.location.search);
  const preview = params.get(searchParams.preview);
  const na = useSelector((state) => state.na);
  const spaces = useSelector((state) => state.na.spaces);
  const workplacestyleSelected = useSelector((state) => state.na.workplacestyleSelected);
  const isExternalAccess = useSelector((state) => state.general.isExternalAccessMode);
  const isReadOnlyMode = useSelector((state) => state.general.isReadOnlyMode);
  const externalAccessConfig = useSelector((state) => state.externalAccessConfig);
  const { t } = useTranslation();
  const workplaceStyle = useMemo(
    () => spaces && spaces.find((space) => space.id === workplacestyleSelected),
    [spaces, workplacestyleSelected],
  );
  const spaceTypes = useMemo(() => workplaceStyle.spaceTypes, [workplaceStyle]);
  const customSpaceTypes = useMemo(() => workplaceStyle.customSpaces, [workplaceStyle]);
  const [openCustomSpaceDrawer, setCustomSpaceOpenDrawer] = useState(false);
  const [openSpaceDrawer, setSpaceOpenDrawer] = useState(false);
  const [customSpaceConfirmModal, setCustomSpaceConfirmModal] = useState({
    state: false,
    customSpace: defaultCustomSpace,
  });
  const [spaceType, setSpaceType] = useState(defaultSpaceType);
  const [customSpace, setCustomSpace] = useState(defaultCustomSpace);
  const [circulation, setCirculation] = useState(workplaceStyle.circulationMultiplier);
  const [grossUpFactor, setGrossUpFactor] = useState(workplaceStyle.buildingGrossUpFactor);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [isCustomSpaceEdit, setIsCustomSpaceEdit] = useState(false);

  const { goToStep } = useStepsWizard({
    naId: na.id,
    preview,
    isExternalAccess,
    accessKey: externalAccessConfig.accessKey,
  });
  const circulationTooltip = (
    <div className={styles.tooltip}>
      <div className={styles.title}>{t('step5.settings.circulationTitle')}</div>
      <div className={styles.description}>
        {t('step5.settings.circulationDescription')}
        <ul>
          {t('step5.circulationTooltip')
            .split('\\n')
            .map((text) => (
              <li key={`circulation-description-${text}`}>{text}</li>
            ))}
        </ul>
      </div>
    </div>
  );
  const grossUpTooltip = (
    <div className={styles.tooltip}>
      <div className={styles.title}>{t('step5.settings.buildingGrossUp')}</div>
      <div className={styles.description}>
        <span>({t('step5.settings.buildingGrossUpDescription1')})</span>(
        {t('step5.settings.buildingGrossUpDescription2')}
        <br />
        {t('step5.settings.buildingGrossUpDescription3')})
      </div>
    </div>
  );

  useEffect(() => {
    if (!isReadOnlyMode) dispatch(actions.warning.setShowWarning(true));
  }, [dispatch, isReadOnlyMode]);

  useEffect(() => {
    setCirculation(workplaceStyle.circulationMultiplier);
    setGrossUpFactor(workplaceStyle.buildingGrossUpFactor);
  }, [workplaceStyle]);

  const toggleSpaceDrawer = (open, toggledSpaceType = defaultSpaceType) => {
    setSpaceType(toggledSpaceType);
    setSpaceOpenDrawer(open);
    setSelectedId(toggledSpaceType.id);
  };

  const toggleCustomSpaceDrawer = (
    open,
    toggledCustomSpace = defaultCustomSpace,
    isEdit = false,
  ) => {
    setCustomSpace(toggledCustomSpace);
    setCustomSpaceOpenDrawer(open);
    setSelectedId(toggledCustomSpace.id);
    setIsCustomSpaceEdit(isEdit);
  };

  const handleChangeWorkplace = (selectedWorkplaceStyle) => {
    dispatch(actions.na.updateWorkplaceStyleSelected(Number(selectedWorkplaceStyle)));
    dispatch(isExternalAccess ? updateExternalAccessNA({ hasChanged: true }) : updateNA);
  };

  const handleRemoveCustomSpaceAction = (confirmIgnore) => {
    setCustomSpaceConfirmModal({ ...customSpaceConfirmModal, state: false });
    if (confirmIgnore) {
      dispatch(actions.na.removeCustomSpace(customSpaceConfirmModal.customSpace));
      dispatch(isExternalAccess ? updateExternalAccessNA({ hasChanged: true }) : updateNA);
    }
  };

  const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#ffffff',
      color: 'rgba(0, 0, 0, 0.87)',
      boxShadow: '0px 0px 5px 0px #8e8e8e',
      width: 340,
    },
  }));

  const cancelSettingsChange = () => {
    setCirculation(workplaceStyle.circulationMultiplier);
    setGrossUpFactor(workplaceStyle.buildingGrossUpFactor);
    setShowSettings(false);
  };

  const applySettings = () => {
    dispatch(
      actions.na.updateSpaceSettings({
        workplacestyleSelected,
        circulationMultiplier: Number(circulation),
        buildingGrossUpFactor: Number(grossUpFactor),
      }),
    );
    dispatch(isExternalAccess ? updateExternalAccessNA({ hasChanged: true }) : updateNA);
    setShowSettings(false);
  };

  const updateDataStore = (step) => {
    if (!isReadOnlyMode) {
      dispatch(actions.na.updateLastStep(step));
      if (step === paths.steps.scorecard) dispatch(actions.na.updateScorecardReached(true));
      dispatch(isExternalAccess ? updateExternalAccessNA({ hasChanged: false }) : updateNA);
    }
  };

  const handleNext = () => {
    updateDataStore(paths.steps.scorecard);
    goToStep({ step: paths.steps.scorecard });
  };
  const handleBack = () => {
    updateDataStore(paths.steps.spaceYouNeed);
    goToStep({ step: paths.steps.spaceYouNeed });
  };

  return (
    <>
      {isReadOnlyMode && !isExternalAccess && <ExternalClientAccessInfo />}
      <div className={styles.container}>
        <div className={styles.column1}>
          <HeaderLogo />
          <div className={styles.row2}>
            <div className={styles.title_configuration_row}>
              <div className={styles.title_text}>{t('step5.heading')}</div>
              <div className={styles.configuration}>
                <Button
                  disabled={isReadOnlyMode}
                  data-testid="ToggleCustomSpaceDrawerButton"
                  className={styles.custom_space_button}
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() => toggleCustomSpaceDrawer(true, getNewCustomSpace())}>
                  {t('step5.spaceDrawer.title')}
                </Button>
                <div className={styles.settings_wrapper}>
                  <Button
                    data-testid="Step5SettingsBtn"
                    variant="contained"
                    className={styles.settings}
                    startIcon={<SettingsIcon />}
                    onClick={() => setShowSettings(!showSettings)}>
                    {t('step5.settings.title')}
                  </Button>
                  {showSettings && (
                    <div data-testid="Step5Settings" className={styles.settings_container}>
                      <div className={styles.settings__label_container}>
                        <span className={styles.settings__label}>
                          {t('step5.settings.circulation')}
                        </span>
                        <HtmlTooltip
                          followCursor
                          title={circulationTooltip}
                          TransitionComponent={Fade}
                          placement="top">
                          <img src={information} alt="" />
                        </HtmlTooltip>
                      </div>
                      <TextField
                        disabled={isReadOnlyMode}
                        data-testid="Step5CirculationText"
                        className={styles.settings__input}
                        value={circulation}
                        onChange={(event) => setCirculation(event.target.value)}
                        inputProps={{
                          allowNegative: true,
                        }}
                        InputProps={{
                          inputComponent: NumberFormatCustom,
                          endAdornment: (
                            <InputAdornment position="start">
                              <PercentOutlinedIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <div className={styles.settings__label_container}>
                        <span className={styles.settings__label}>
                          {t('step5.settings.buildingGrossUp')}
                        </span>
                        <HtmlTooltip
                          followCursor
                          title={grossUpTooltip}
                          TransitionComponent={Fade}
                          placement="top">
                          <img src={information} alt="" />
                        </HtmlTooltip>
                      </div>

                      <TextField
                        disabled={isReadOnlyMode}
                        data-testid="Step5GrossUpFactorText"
                        className={`${styles.settings__input}`}
                        value={grossUpFactor}
                        onChange={(event) => setGrossUpFactor(event.target.value)}
                        inputProps={{
                          allowNegative: true,
                        }}
                        InputProps={{
                          inputComponent: NumberFormatCustom,
                          endAdornment: (
                            <InputAdornment position="start">
                              <PercentOutlinedIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Button
                        disabled={isReadOnlyMode}
                        data-testid="Step5SaveBtn"
                        className={`${styles.settings__btn} ${styles.settings__btn_dark}`}
                        size="small"
                        variant="contained"
                        onClick={() => applySettings()}>
                        {t('common.apply')}
                      </Button>
                      <Button
                        data-testid="Step5CancleBtn"
                        onClick={() => cancelSettingsChange()}
                        className={styles.settings__btn}
                        size="small"
                        variant="contained">
                        {t('common.cancel')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.description}>{t('step5.description')}</div>
            <div data-testid="Step5Tabs" className={styles.workplace_style_tab_menu}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  disabled={isReadOnlyMode}
                  value={workplacestyleSelected}
                  TabIndicatorProps={{
                    style: {
                      backgroundColor: '#3E3E3E',
                    },
                  }}
                  onChange={(_, value) => handleChangeWorkplace(value)}>
                  {Array.isArray(spaces)
                    ? spaces.map((space) => (
                        <Tab
                          disabled={isReadOnlyMode}
                          className={styles.workplace_style_tab}
                          disableRipple
                          label={t(space.name)}
                          value={space.id}
                          key={weakKey(space)}></Tab>
                      ))
                    : []}
                </Tabs>
              </Box>
              <div data-testid="Step5SpaceType" className={styles.workplace_content}>
                {Array.isArray(customSpaceTypes)
                  ? customSpaceTypes.map((customSpaceType) => (
                      <div
                        className={`${styles.space_rows} ${
                          customSpaceType.id === selectedId ? styles.hovered : ''
                        }`}
                        key={weakKey(customSpaceType)}>
                        <div className={styles.customspace_row_name}>{customSpaceType.name}</div>
                        <div className={styles.customspace_row_units}>
                          {addThousandSeparator(
                            customSpaceType.quantity,
                            getFormatSeparatorsOptions(),
                          ) +
                            ' ' +
                            t('step5.units')}
                        </div>
                        <div className={styles.customspace_row_units}>
                          {addThousandSeparator(
                            customSpaceType.quantity *
                              customSpaceType.width *
                              customSpaceType.height,
                            getFormatSeparatorsOptions(),
                          ) +
                            ' ' +
                            t('step5.total') +
                            ' ' +
                            getUnits().area}
                        </div>
                        <div className={styles.space_row_edit_icon}>
                          <IconButton
                            data-testid="Step5CustomSpaceEdit"
                            aria-label="delete"
                            size="small"
                            onClick={() => toggleCustomSpaceDrawer(true, customSpaceType, true)}>
                            {isReadOnlyMode ? <SearchIcon /> : <EditIcon />}
                          </IconButton>
                        </div>
                        <div className={styles.space_row_edit_icon}>
                          {!isReadOnlyMode ? (
                            <IconButton
                              disabled={isReadOnlyMode}
                              data-testid="Step5CustomSpaceDelete"
                              aria-label="delete"
                              size="small"
                              onClick={() =>
                                setCustomSpaceConfirmModal({
                                  state: true,
                                  customSpace: customSpaceType,
                                })
                              }>
                              <DeleteIcon />
                            </IconButton>
                          ) : null}
                        </div>
                      </div>
                    ))
                  : []}
                {spaceTypes.map((spType, index) => (
                  <div
                    className={`${styles.space_rows} ${
                      spType.id === selectedId ? styles.hovered : ''
                    }`}
                    key={weakKey(spType)}>
                    <div className={styles.space_row_name}>{t(spType.name)}</div>
                    <div className={styles.space_row_units}>
                      {getQuantity(spType, getFormatSeparatorsOptions()) + ' ' + t('step5.units')}
                    </div>
                    <div className={styles.space_row_units}>
                      {`${getTotalSF(spType, getFormatSeparatorsOptions())} ${t('step5.total')} ${
                        getUnits().area
                      }`}
                    </div>
                    <div className={styles.space_row_edit_icon}>
                      <IconButton
                        data-testid={`ToggleSpaceDrawerButton-${index}`}
                        aria-label="delete"
                        size="small"
                        onClick={() => toggleSpaceDrawer(true, spType)}>
                        {isReadOnlyMode ? <SearchIcon /> : <EditIcon />}
                      </IconButton>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.row3}>
            <Footer
              title={t('step5.footerTitle')}
              description={t('step5.footerDescription')}
              stepsHighlighted="5"
              handleback={handleBack}
              handlenext={handleNext}></Footer>
          </div>
        </div>
        <div className={styles.column2}>
          <Summary office_type_id={workplacestyleSelected} />
        </div>
        <SpaceDrawer
          isExternalAccess={isExternalAccess}
          disabled={isReadOnlyMode}
          quantityBounds={quantityBounds}
          spaceType={spaceType}
          state={openSpaceDrawer}
          toggleDrawer={toggleSpaceDrawer}
        />
        <CustomSpaceDrawer
          isExternalAccess={isExternalAccess}
          disabled={isReadOnlyMode}
          quantityBounds={quantityBounds}
          customSpace={customSpace}
          state={openCustomSpaceDrawer}
          toggleDrawer={toggleCustomSpaceDrawer}
          isEdit={isCustomSpaceEdit}
        />
        <ConfirmationModal
          open={customSpaceConfirmModal.state}
          onAction={handleRemoveCustomSpaceAction}
          title={t('step5.spaceDrawer.delete')}
          description={t('step5.spaceDrawer.deleteConfirmation', {
            spaceName: customSpaceConfirmModal.customSpace.name,
          })}></ConfirmationModal>
      </div>
    </>
  );
}
